import React, { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LOGO_MARK } from "@/lib/brand";
import { track } from "@/lib/analytics";

const isStandalone = () =>
  (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
  window.navigator.standalone === true;

const isIos = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem("bs_install_dismissed")) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    let iosTimer;
    if (isIos()) {
      iosTimer = window.setTimeout(() => {
        setIosHint(true);
        setShow(true);
      }, 6000);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      if (iosTimer) window.clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem("bs_install_dismissed", "1");
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return;
    track("install_prompt_clicked", {});
    deferred.prompt();
    const choice = await deferred.userChoice;
    track(choice.outcome === "accepted" ? "app_installed" : "install_dismissed", {});
    setDeferred(null);
    setShow(false);
    localStorage.setItem("bs_install_dismissed", "1");
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:right-auto sm:max-w-xs z-[55] bg-white border border-slate-200 shadow-xl rounded-xl p-4"
      data-testid="install-prompt"
    >
      <button
        onClick={dismiss}
        aria-label="Dismiss install prompt"
        data-testid="install-dismiss"
        className="absolute top-2.5 right-2.5 text-slate-300 hover:text-slate-500"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3">
        <span className="h-10 w-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0 p-1">
          <img src={LOGO_MARK} alt="Trade Off" className="h-full w-full object-contain" />
        </span>
        <div className="pr-4">
          <p className="font-display font-semibold text-slate-900 text-sm">Install Trade Off</p>
          {iosHint ? (
            <p className="text-sm text-slate-600 mt-1 flex items-center gap-1 flex-wrap">
              Tap <Share className="h-3.5 w-3.5 inline text-blue-600" /> then <span className="font-medium">Add to Home Screen</span> to open takeoffs on-site.
            </p>
          ) : (
            <>
              <p className="text-sm text-slate-600 mt-1">Add it to your home screen to open jobs fast — even with patchy signal.</p>
              <div className="mt-3 flex gap-2">
                <Button size="sm" onClick={install} data-testid="install-accept" className="bg-blue-600 hover:bg-blue-700">
                  <Download className="h-4 w-4 mr-1.5" /> Install
                </Button>
                <Button size="sm" variant="outline" onClick={dismiss} className="border-slate-300" data-testid="install-later">Not now</Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
